import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash2, Edit3, Search, Database, X, Wheat, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "all", label: "Tutti" },
  { key: "pasta", label: "Pasta" },
  { key: "riso", label: "Riso" },
  { key: "farine", label: "Farine" },
  { key: "pane", label: "Pane" },
  { key: "verdura", label: "Verdura" },
  { key: "frutta", label: "Frutta" },
  { key: "carne", label: "Carne" },
  { key: "pesce", label: "Pesce" },
  { key: "latticini", label: "Latticini" },
  { key: "dispensa", label: "Dispensa" },
  { key: "vini", label: "Vini cucina" },
  { key: "altro", label: "Altro" },
];

const fmtEur = (n) => `€ ${Number(n).toFixed(2)}`;

export default function Ingredients() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const r = await api.get("/ingredients", { params: { category: cat, search: search || undefined } });
    setItems(r.data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat]);

  const onSearch = (e) => { e.preventDefault(); load(); };

  const handleSave = async (ing) => {
    try {
      if (ing.id) await api.put(`/ingredients/${ing.id}`, ing);
      else await api.post("/ingredients", ing);
      toast.success("Ingrediente salvato");
      setEditing(null);
      load();
    } catch { toast.error("Errore"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo ingrediente?")) return;
    await api.delete(`/ingredients/${id}`);
    load();
  };

  const handleReseed = async () => {
    if (!window.confirm("Ricaricare il database con gli ingredienti italiani standard? Questa azione cancellerà tutti gli ingredienti esistenti.")) return;
    await api.post("/ingredients/seed");
    toast.success("Database ingredienti ricaricato");
    load();
  };

  const handleClearAll = async () => {
    if (!window.confirm("⚠️ ATTENZIONE: cancellare TUTTI gli ingredienti dal database? Questa azione non è reversibile.")) return;
    await api.delete("/ingredients");
    toast.success("Database ingredienti svuotato");
    load();
  };

  return (
    <div className="space-y-8" data-testid="ingredients-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[#2563EB] font-semibold font-ui">Magazzino</p>
          <h1 className="font-display text-4xl md:text-5xl text-[#0F2741] tracking-tight mt-2">Database Ingredienti</h1>
          <p className="text-slate-500 mt-2">{items.length} ingredienti · prezzi medi mercato italiano (€/unità)</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={handleClearAll} data-testid="btn-clear-ingredients" className="btn-ghost border border-rose-200 text-rose-600 hover:bg-rose-50">
            <AlertTriangle className="w-4 h-4" /> Cancella DB
          </button>
          <button onClick={handleReseed} data-testid="btn-reseed-ingredients" className="btn-ghost border border-slate-200">
            <Database className="w-4 h-4" /> Ricarica DB
          </button>
          <button onClick={() => setEditing({})} data-testid="btn-new-ingredient" className="btn-primary">
            <Plus className="w-4 h-4" /> Nuovo ingrediente
          </button>
        </div>
      </div>

      <form onSubmit={onSearch} className="flex gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca un ingrediente..." className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-200 focus:ring-2 focus:ring-[#2563EB] outline-none text-sm" />
        </div>
        <button type="submit" className="btn-secondary">Cerca</button>
      </form>

      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((c) => (
          <button key={c.key} onClick={() => setCat(c.key)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${cat === c.key ? "bg-[#0F2741] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#2563EB]"}`}>
            {c.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 tracking-wider">
            <tr>
              <th className="text-left px-6 py-3">Nome</th>
              <th className="text-left px-6 py-3">Categoria</th>
              <th className="text-center px-6 py-3">Unità</th>
              <th className="text-right px-6 py-3">Prezzo medio</th>
              <th className="text-right px-6 py-3">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-6 py-12 text-center text-slate-400">Caricamento...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="px-6 py-12 text-center">
                <Wheat className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                <p className="text-slate-500">Nessun ingrediente</p>
              </td></tr>
            ) : items.map((d) => (
              <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                <td className="px-6 py-3 font-medium text-[#0F2741] capitalize">{d.name}</td>
                <td className="px-6 py-3"><span className="text-xs px-2 py-1 rounded-full bg-[#EFF6FF] text-[#1D4ED8] capitalize">{d.category}</span></td>
                <td className="px-6 py-3 text-center text-slate-500 uppercase text-xs">{d.unit}</td>
                <td className="px-6 py-3 text-right font-semibold text-[#0F2741]">{fmtEur(d.price_eur)}</td>
                <td className="px-6 py-3 text-right">
                  <button onClick={() => setEditing(d)} className="text-slate-400 hover:text-[#2563EB] p-1.5"><Edit3 className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(d.id)} className="text-slate-400 hover:text-rose-500 p-1.5"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing !== null && <IngModal initial={editing} onClose={() => setEditing(null)} onSave={handleSave} />}
    </div>
  );
}

function IngModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState({
    id: initial.id,
    name: initial.name || "",
    category: initial.category || "altro",
    unit: initial.unit || "kg",
    price_eur: initial.price_eur || 0,
  });

  // Auto-scroll page to top so the modal is always visible, lock body scroll
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    window.scrollTo({ top: 0, behavior: "smooth" });
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  const submit = (e) => {
    e.preventDefault();
    onSave({ ...form, price_eur: parseFloat(form.price_eur) || 0 });
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 overflow-y-auto p-4 sm:p-8" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-6 w-full max-w-md space-y-3 mx-auto my-auto shadow-2xl animate-in fade-in slide-in-from-top-4 duration-200" data-testid="ingredient-modal">
        <div className="flex justify-between items-center">
          <h3 className="font-display text-2xl text-[#0F2741]">{form.id ? "Modifica" : "Nuovo"} ingrediente</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <input required value={form.name} placeholder="Nome ingrediente" data-testid="input-ingredient-name" onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200" />
        <div className="grid grid-cols-2 gap-3">
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200">
            {CATEGORIES.filter((c) => c.key !== "all").map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200">
            <option value="kg">kg</option>
            <option value="L">L (litro)</option>
            <option value="pz">pz</option>
          </select>
        </div>
        <input type="number" step="0.01" required value={form.price_eur} placeholder="Prezzo per unità (€)" onChange={(e) => setForm({ ...form, price_eur: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200" />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-ghost">Annulla</button>
          <button type="submit" className="btn-primary">Salva</button>
        </div>
      </form>
    </div>
  );
}
